/**
 * Session Management Utilities
 *
 * Provides functions for saving and managing breathing sessions
 * in the Supabase database.
 */

import { supabase } from './supabase';
import { ensureUserProfile } from './auth';
import { type BreathingStyle } from '@/types/breathing';

/**
 * Parameters for saving a breathing session
 */
export interface SaveSessionParams {
  user_id: string;
  total_duration: number;
  cycles_completed: number;
  breathing_ratio_id?: string | null;
  background_videos_id?: string | null;
  voice_guidance: boolean;
  phase_chime: boolean;
  background_sound: boolean;
  intro_voice: boolean;
  leaderboard_eligibility: boolean;
  // Affirmation support (optional)
  affirmation?: boolean;
  affirmation_type?: string | null;
  affirmation_mode?: 'regular' | 'subliminal' | null;
  affirmation_whisper_mode?: boolean;
  play_in_background?: boolean;
  hide_cycle_count?: boolean;
  hide_time_left?: boolean;
  // Accept camelCase too for callers using different naming styles
  playInBackground?: boolean;
  hideCycleCount?: boolean;
  hideTimeLeft?: boolean;
  session_date?: string;
  notes?: string;
  breathing_style?: BreathingStyle;
  breathing_style_id?: string | null;
  music_enabled?: boolean;
  music_track_id?: string | null;
}

/**
 * Represents a breathing session record
 */
export interface BreathingSession {
  id: string;
  user_id: string;
  total_duration: number;
  cycles_completed: number;
  breathing_ratio_id: string | null;
  background_videos_id: string | null;
  voice_guidance: boolean;
  phase_chime: boolean;
  background_sound: boolean;
  intro_voice: boolean;
  leaderboard_eligibility: boolean;
  affirmation: boolean;
  affirmation_type: string | null;
  affirmation_mode: 'regular' | 'subliminal' | null;
  affirmation_whisper_mode: boolean;
  play_in_background: boolean;
  hide_cycle_count: boolean;
  hide_time_left: boolean;
  music_enabled: boolean;
  music_track_id: string | null;
  session_date: string;
  notes: string | null;
  created_at: string;
  user_timezone?: string | null;
  user_timezone_offset_minutes?: number | null;
  breathing_style_id?: string | null;
}

/**
 * Save a breathing session to the database
 *
 * @param params - Session parameters including duration, cycles, settings, and eligibility
 * @returns The created session record
 * @throws Error if the save operation fails
 */
export async function saveSession(
  params: SaveSessionParams
): Promise<BreathingSession> {
  const now = new Date();

  // Ensure a public.users row exists for the current auth user so the
  // foreign key constraint on `sessions.user_id` won't fail for new users.
  await ensureUserProfile();

  let userTimezone: string | null = null;
  try {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    }
  } catch {
    userTimezone = null;
  }

  const userTimezoneOffsetMinutes = now.getTimezoneOffset();

  const sessionData = {
    user_id: params.user_id,
    total_duration: params.total_duration,
    cycles_completed: params.cycles_completed,
    breathing_ratio_id: params.breathing_ratio_id || null,
    background_videos_id: params.background_videos_id || null,
    voice_guidance: params.voice_guidance,
    phase_chime: params.phase_chime,
    background_sound: params.background_sound,
    intro_voice: params.intro_voice,
    leaderboard_eligibility: params.leaderboard_eligibility,
    // Affirmations: default to false when not provided
    affirmation: params.affirmation ?? false,
    affirmation_type: params.affirmation_type ?? null,
    affirmation_mode: params.affirmation_mode ?? null,
    affirmation_whisper_mode: params.affirmation_whisper_mode ?? false,
    // Support both snake_case and camelCase caller conventions
    play_in_background:
      params.play_in_background ?? params.playInBackground ?? false,
    hide_cycle_count: params.hide_cycle_count ?? params.hideCycleCount ?? false,
    hide_time_left: params.hide_time_left ?? params.hideTimeLeft ?? false,
    session_date: params.session_date || now.toISOString(),
    notes: params.notes || null,
    user_timezone: userTimezone,
    user_timezone_offset_minutes: userTimezoneOffsetMinutes,
    breathing_style_id: params.breathing_style_id ?? null,
    music_enabled: params.music_enabled ?? false,
    music_track_id: params.music_track_id ?? null,
  };

  let { data, error } = await supabase
    .from('sessions')
    .insert(sessionData)
    .select()
    .single();

  if (error) {
    // If the error is about a missing column, try falling back without the newer columns.
    // This allows the app to function even if the DB schema hasn't been updated yet.
    if (
      error.message?.includes(
        "Could not find the 'affirmation_whisper_mode' column"
      ) ||
      error.message?.includes("Could not find the 'affirmation_mode' column") ||
      error.message?.includes("Could not find the 'music_enabled' column") ||
      error.message?.includes(
        "Could not find the 'play_in_background' column"
      ) ||
      error.message?.includes("Could not find the 'hide_cycle_count' column") ||
      error.message?.includes("Could not find the 'hide_time_left' column")
    ) {
      const fallbackData = { ...sessionData };
      delete (fallbackData as any).affirmation_whisper_mode;
      delete (fallbackData as any).affirmation_mode;
      delete (fallbackData as any).music_enabled;
      delete (fallbackData as any).music_track_id;
      delete (fallbackData as any).play_in_background;
      delete (fallbackData as any).playInBackground;
      delete (fallbackData as any).hide_cycle_count;
      delete (fallbackData as any).hideCycleCount;
      delete (fallbackData as any).hide_time_left;
      delete (fallbackData as any).hideTimeLeft;

      const retry = await supabase
        .from('sessions')
        .insert(fallbackData)
        .select()
        .single();

      if (!retry.error) {
        data = retry.data;
        error = null;
      } else {
        // If retry also fails, propagate the new error
        error = retry.error;
      }
    }
  }

  if (error) {
    console.error('Error saving session:', error);
    throw new Error(`Failed to save session: ${error.message}`);
  }

  if (!data) {
    throw new Error('Session saved but no data returned');
  }

  // Debug: try to read the user's streak row right after inserting a session.
  // This helps diagnose why `user_streaks` may not be created/updated by the DB trigger.
  try {
    const { data: streakData, error: streakError } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', params.user_id)
      .maybeSingle();

    if (streakError) {
      // Only log — don't throw. This is strictly debug output.
      // eslint-disable-next-line no-console
      console.debug('saveSession: failed to read user_streaks', {
        streakError,
      });
    } else {
      // eslint-disable-next-line no-console
      console.debug('saveSession: user_streaks after insert', { streakData });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('saveSession: unexpected error reading user_streaks', err);
  }

  return data as BreathingSession;
}

/**
 * Get all sessions for a specific user
 *
 * @param userId - The user's ID
 * @param limit - Optional limit on number of sessions to return
 * @returns Array of session records
 */
export async function getUserSessions(
  userId: string,
  limit?: number
): Promise<BreathingSession[]> {
  let query = supabase
    .from('sessions')
    .select('*')
    .eq('user_id', userId)
    .order('session_date', { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching user sessions:', error);
    throw new Error(`Failed to fetch sessions: ${error.message}`);
  }

  return (data as BreathingSession[]) || [];
}

/**
 * Update only the notes field for a session
 */
export async function updateSessionNotes(
  sessionId: string,
  notes: string | null
): Promise<BreathingSession> {
  const { data, error } = await supabase
    .from('sessions')
    .update({ notes })
    .eq('id', sessionId)
    .select()
    .single();

  if (error) {
    console.error('Error updating session notes:', error);
    throw new Error(`Failed to update session notes: ${error.message}`);
  }

  if (!data) {
    throw new Error('Session notes update succeeded but no data returned');
  }

  return data as BreathingSession;
}

/**
 * Get leaderboard-eligible sessions for a user
 *
 * @param userId - The user's ID
 * @param limit - Optional limit on number of sessions to return
 * @returns Array of leaderboard-eligible session records
 */
export async function getUserLeaderboardSessions(
  userId: string,
  limit?: number
): Promise<BreathingSession[]> {
  let query = supabase
    .from('sessions')
    .select('*')
    .eq('user_id', userId)
    .eq('leaderboard_eligibility', true)
    .order('session_date', { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching leaderboard sessions:', error);
    throw new Error(`Failed to fetch leaderboard sessions: ${error.message}`);
  }

  return (data as BreathingSession[]) || [];
}

/**
 * Delete a session by ID
 *
 * @param sessionId - The session's ID
 * @returns True if deleted successfully
 */
export async function deleteSession(sessionId: string): Promise<boolean> {
  const { error } = await supabase
    .from('sessions')
    .delete()
    .eq('id', sessionId);

  if (error) {
    console.error('Error deleting session:', error);
    throw new Error(`Failed to delete session: ${error.message}`);
  }

  return true;
}

type PopularityColumn = 'breathing_ratio_id' | 'background_videos_id';

async function getMostPopularSessionValue(
  column: PopularityColumn,
  candidateIds: string[]
): Promise<string | null> {
  if (!candidateIds || candidateIds.length === 0) {
    return null;
  }

  const uniqueIds = Array.from(new Set(candidateIds.filter(Boolean)));
  if (uniqueIds.length === 0) return null;

  try {
    const counts = await Promise.all(
      uniqueIds.map(async (id) => {
        const { count, error } = await supabase
          .from('sessions')
          .select(column, { head: true, count: 'exact' })
          .eq(column, id);

        if (error) {
          throw error;
        }

        return {
          id,
          count: count ?? 0,
        };
      })
    );

    const top = counts.reduce<{
      id: string;
      count: number;
    } | null>((currentTop, next) => {
      if (next.count <= 0) return currentTop;
      if (!currentTop || next.count > currentTop.count) return next;
      return currentTop;
    }, null);

    return top?.id ?? null;
  } catch (error) {
    console.error(`Failed to load popularity for ${column}`, error);
    return null;
  }
}

export async function getMostPopularBreathingRatioId(
  candidateIds: string[]
): Promise<string | null> {
  return getMostPopularSessionValue('breathing_ratio_id', candidateIds);
}

export async function getMostPopularBackgroundVideoId(
  candidateIds: string[]
): Promise<string | null> {
  return getMostPopularSessionValue('background_videos_id', candidateIds);
}
